import React, { useState, useEffect } from 'react';
import Navigation from '../../Components/Navigation';
import './Support_page.css';
import { FiMail, FiClock, FiUser, FiX, FiCheck, FiPhone, FiTag } from 'react-icons/fi';

const Support_page = () => {
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [activeTab, setActiveTab] = useState('support');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [supportMessages, setSupportMessages] = useState([]);
  const [complaints, setComplaints] = useState([]);
  
  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [supportResponse, complaintsResponse] = await Promise.all([
          fetch('https://tstock.trillionstechsolutions.com/enquiry-files/all'),
          fetch('https://tstock.trillionstechsolutions.com/complaints/all')
        ]);

        if (!supportResponse.ok || !complaintsResponse.ok) {
          throw new Error('Failed to fetch data');
        }

        const supportData = await supportResponse.json();
        const complaintsData = await complaintsResponse.json();

        setSupportMessages(supportData.data);
        setComplaints(complaintsData.data);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const toggleResolution = async (id, type) => {
    try {
      if (type === 'support') {
        const response = await fetch(`https://tstock.trillionstechsolutions.com/enquiry-files/${id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: supportMessages.find(m => m._id === id).status === 'Resolved' ? 'Pending' : 'Resolved'
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to update status');
        }

        setSupportMessages(prev =>
          prev.map(message =>
            message._id === id
              ? { ...message, status: message.status === 'Resolved' ? 'Pending' : 'Resolved' }
              : message
          )
        );
      } else {
        const response = await fetch(`https://tstock.trillionstechsolutions.com/complaint/${id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: complaints.find(c => c._id === id).status === 'Resolved' ? 'Pending' : 'Resolved'
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to update status');
        }

        setComplaints(prev =>
          prev.map(complaint =>
            complaint._id === id
              ? { ...complaint, status: complaint.status === 'Resolved' ? 'Pending' : 'Resolved' }
              : complaint
          )
        );
      }
    } catch (err) {
      setError(err.message);
      console.error('Error updating status:', err);
    }
  };

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  if (loading) {
    return (
      <div className="page-container">
        <Navigation />
        <div className="loading-state">
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <Navigation />
        <div className="error-state">
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const renderSupportMessages = () => (
    <div className="support-content">
      {/* Messages List Section */}
      <div className="messages-list-section">
        <div className="section-header">
          <h2>Support Messages</h2>
          <div className="filter-stats">
            <span className="stat-item">
              Total: {supportMessages.length}
            </span>
            <span className="stat-item">
              Pending: {supportMessages.filter(m => m.status === 'Pending').length}
            </span>
          </div>
        </div>

        <div className="messages-list">
          {supportMessages.map(message => (
            <div 
              key={message._id} 
              className={`message-card ${selectedMessage?._id === message._id ? 'selected' : ''}`}
              onClick={() => setSelectedMessage(message)}
            >
              <div className="message-card-header">
                <h3>Enquiry #{message.enquiryNumber}</h3>
                <div className="message-badges">
                  <span className={`status-badge ${message.status.toLowerCase()}`}>
                    {message.status}
                  </span>
                </div>
              </div>
              
              <div className="message-meta">
                <span className="meta-item">
                  <FiUser className="meta-icon" />
                  {message.fullName}
                </span>
                <span className="meta-item">
                  <FiPhone className="meta-icon" />
                  {message.phoneNumber}
                </span>
                <span className="meta-item">
                  <FiClock className="meta-icon" />
                  {formatDate(message.createdAt)}
                </span>
              </div>
              
              <p className="message-preview">
                {message.message.substring(0, 100)}...
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Message Detail Section */}
      <div className="message-detail-section">
        {selectedMessage ? (
          <>
            <div className="detail-header">
              <h2>Enquiry #{selectedMessage.enquiryNumber}</h2>
              <button 
                className="close-detail-btn"
                onClick={() => setSelectedMessage(null)}
              >
                <FiX />
              </button>
            </div>

            <div className="detail-content">
              <div className="detail-meta">
                <div className="meta-group">
                  <label>Full Name:</label>
                  <div className="meta-value">
                    {selectedMessage.fullName}
                  </div>
                </div>
                
                <div className="meta-group">
                  <label>Email:</label>
                  <div className="meta-value">
                    {selectedMessage.emailAddress}
                  </div>
                </div>

                <div className="meta-group">
                  <label>Phone:</label>
                  <div className="meta-value">
                    {selectedMessage.phoneNumber}
                  </div>
                </div>
                
                <div className="meta-group">
                  <label>Date:</label>
                  <div className="meta-value">
                    {formatDate(selectedMessage.createdAt)}
                  </div>
                </div>
                
                <div className="meta-group">
                  <label>Status:</label>
                  <div className="meta-value">
                    <span className={`status-badge ${selectedMessage.status.toLowerCase()}`}>
                      {selectedMessage.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="message-content">
                <label>Message:</label>
                <p>{selectedMessage.message}</p>
              </div>

              <div className="detail-actions">
                <button
                  className={`resolution-btn ${selectedMessage.status === 'Resolved' ? 'resolved' : 'pending'}`}
                  onClick={() => toggleResolution(selectedMessage._id, 'support')}
                >
                  <FiCheck />
                  Mark as {selectedMessage.status === 'Resolved' ? 'Pending' : 'Resolved'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="no-message-selected">
            <FiMail className="no-message-icon" />
            <p>Select a message to view details</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderComplaints = () => (
    <div className="support-content">
      {/* Complaints List Section */}
      <div className="messages-list-section">
        <div className="section-header">
          <h2>User Complaints</h2>
          <div className="filter-stats">
            <span className="stat-item">
              Total: {complaints.length}
            </span>
            <span className="stat-item">
              Pending: {complaints.filter(c => c.status === 'Pending').length}
            </span>
          </div>
        </div>

        <div className="messages-list">
          {complaints.map(complaint => (
            <div 
              key={complaint._id} 
              className={`message-card ${selectedComplaint?._id === complaint._id ? 'selected' : ''}`}
              onClick={() => setSelectedComplaint(complaint)}
            >
              <div className="message-card-header">
                <h3>Ticket #{complaint.ticketNumber}</h3>
                <div className="message-badges">
                  <span className={`status-badge ${complaint.status.toLowerCase()}`}>
                    {complaint.status}
                  </span>
                </div>
              </div>
              
              <div className="message-meta">
                <span className="meta-item">
                  <FiUser className="meta-icon" />
                  {complaint.fullName}
                </span>
                <span className="meta-item">
                  <FiPhone className="meta-icon" />
                  {complaint.phoneNumber}
                </span>
                <span className="meta-item">
                  <FiTag className="meta-icon" />
                  {complaint.complaintType}
                </span>
                <span className="meta-item">
                  <FiClock className="meta-icon" />
                  {formatDate(complaint.createdAt)}
                </span>
              </div>
              
              <div className="message-preview">
                <strong>Subject:</strong> {complaint.subject}
                <p>{complaint.complaintDetails.substring(0, 100)}...</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Complaint Detail Section */}
      <div className="message-detail-section">
        {selectedComplaint ? (
          <>
            <div className="detail-header">
              <h2>Ticket #{selectedComplaint.ticketNumber}</h2>
              <button 
                className="close-detail-btn"
                onClick={() => setSelectedComplaint(null)}
              >
                <FiX />
              </button>
            </div>

            <div className="detail-content">
              <div className="detail-meta">
                <div className="meta-group">
                  <label>Full Name:</label>
                  <div className="meta-value">
                    {selectedComplaint.fullName}
                  </div>
                </div>
                
                <div className="meta-group">
                  <label>Email:</label>
                  <div className="meta-value">
                    {selectedComplaint.emailAddress}
                  </div>
                </div>

                <div className="meta-group">
                  <label>Phone:</label>
                  <div className="meta-value">
                    {selectedComplaint.phoneNumber}
                  </div>
                </div>

                <div className="meta-group">
                  <label>Type:</label>
                  <div className="meta-value">
                    {selectedComplaint.complaintType}
                  </div>
                </div>

                <div className="meta-group">
                  <label>Subject:</label>
                  <div className="meta-value">
                    {selectedComplaint.subject}
                  </div>
                </div>
                
                <div className="meta-group">
                  <label>Date:</label>
                  <div className="meta-value">
                    {formatDate(selectedComplaint.createdAt)}
                  </div>
                </div>
                
                <div className="meta-group">
                  <label>Status:</label>
                  <div className="meta-value">
                    <span className={`status-badge ${selectedComplaint.status.toLowerCase()}`}>
                      {selectedComplaint.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="message-content">
                <label>Complaint Details:</label>
                <p>{selectedComplaint.complaintDetails}</p>
              </div>

              {selectedComplaint.attachedDocuments && (
                <div className="attached-documents">
                  <label>Attached Documents:</label>
                  <div className="document-preview">
                    <img 
                      src={`https://tstock.trillionstechsolutions.com${selectedComplaint.attachedDocuments}`} 
                      alt="Attached document"
                      className="document-image"
                    />
                  </div>
                </div>
              )}

              <div className="detail-actions">
                <button
                  className={`resolution-btn ${selectedComplaint.status === 'Resolved' ? 'resolved' : 'pending'}`}
                  onClick={() => toggleResolution(selectedComplaint._id, 'complaints')}
                >
                  <FiCheck />
                  Mark as {selectedComplaint.status === 'Resolved' ? 'Pending' : 'Resolved'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="no-message-selected">
            <FiMail className="no-message-icon" />
            <p>Select a complaint to view details</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="page-container">
      <Navigation />
      <div className="support-container">
        <div className="tabs">
          <button
            className={`tab-btn ${activeTab === 'support' ? 'active' : ''}`}
            onClick={() => setActiveTab('support')}
          >
            Support Messages
          </button>
          <button
            className={`tab-btn ${activeTab === 'complaints' ? 'active' : ''}`}
            onClick={() => setActiveTab('complaints')}
          >
            User Complaints
          </button>
        </div>
        {activeTab === 'support' ? renderSupportMessages() : renderComplaints()}
      </div>
    </div>
  );
};

export default Support_page;
